import { useEffect, useRef } from 'react';

const differentials = [
  {
    num: '01',
    icon: 'ri-user-heart-line',
    title: 'Atendimento Personalizado',
    description:
      'Cada cliente é único. Dedicamos atenção integral à sua causa, compreendendo profundamente cada situação antes de definir a estratégia jurídica mais adequada.',
  },
  {
    num: '02',
    icon: 'ri-focus-3-line',
    title: 'Estratégia Individualizada',
    description:
      'Não existe caso igual. Desenvolvemos uma estratégia jurídica construída especificamente para a sua situação, com base em análise técnica detalhada e experiência prática.',
  },
  {
    num: '03',
    icon: 'ri-flashlight-line',
    title: 'Agilidade e Transparência',
    description:
      'Mantemos você informado em cada etapa do processo. Nosso compromisso é com a clareza, a rapidez e a comunicação eficiente durante toda a relação jurídica.',
  },
  {
    num: '04',
    icon: 'ri-shield-check-line',
    title: 'Ética e Técnica',
    description:
      'Atuamos com rigoroso respeito ao Código de Ética da OAB, garantindo uma advocacia responsável, técnica e comprometida com os princípios fundamentais do Direito.',
  },
];

export default function Differentials() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-diff').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="diferenciais" ref={sectionRef} className="py-28 lg:py-36 bg-ice relative overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C8A24A' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div
          className="fade-diff mb-20"
          style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-px bg-gold" />
            <span className="text-gold font-sans font-600 text-xs tracking-[0.25em] uppercase">Nossos Diferenciais</span>
          </div>
          <div className="lg:flex lg:items-end lg:justify-between">
            <h2 className="font-serif text-deep text-4xl lg:text-5xl font-700 leading-tight max-w-lg">
              Por que escolher<br />
              <em className="text-gold font-600">Benner Martins</em>
            </h2>
            <p className="font-sans text-graphite/70 text-sm font-300 max-w-xs leading-relaxed mt-4 lg:mt-0 lg:text-right">
              Comprometimento com resultados éticos e excelência técnica em cada causa.
            </p>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {differentials.map((item, i) => (
            <div
              key={item.num}
              className="fade-diff group relative bg-white rounded-lg p-8 transition-all duration-400 hover:-translate-y-1 overflow-hidden"
              style={{
                opacity: 0,
                transform: 'translateY(30px)',
                transition: `opacity 0.8s ease ${i * 0.15}s, transform 0.4s ease`,
                border: '1px solid rgba(200,162,74,0.15)',
              }}
            >
              {/* Background Number */}
              <span
                className="absolute top-3 right-4 font-serif font-700 text-8xl leading-none select-none pointer-events-none"
                style={{ color: 'rgba(200,162,74,0.08)' }}
              >
                {item.num}
              </span>

              {/* Icon */}
              <div className="w-12 h-12 flex items-center justify-center border border-gold/30 rounded-md mb-6 group-hover:border-gold transition-colors duration-300">
                <i className={`${item.icon} text-gold text-xl`} />
              </div>

              {/* Content */}
              <h3 className="font-sans text-deep text-base font-600 mb-3 leading-snug">{item.title}</h3>
              <p className="font-sans text-graphite/70 text-sm font-300 leading-relaxed">{item.description}</p>

              {/* Bottom accent */}
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gold transition-all duration-500 group-hover:w-full" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
